import { Box, LinearProgress, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SurveyStatsStatusEnum, SurveyEngagementFunnelDto } from '@families-link/shared';
import { AnalyticsChartColors } from '../../enums/analytics-chart-colors.enum';
import { FadeSpinner } from '../../../_core/components/_ui/fade-spinner';

type SurveyFunnelLinearChartProps = {
	data: SurveyEngagementFunnelDto[];
	title?: string;
	useRandomColor?: boolean;
	colorsEnum?: any;
	loading?: boolean;
};

type SurveyFunnelRowProps = {
	label: string;
	percentage: number;
	familiesAmount: number;
	color?: string;
};

export const SurveyEngagementFunnelLinearChart = (props: SurveyFunnelLinearChartProps) => {
	const { data, loading } = props;
	const [t] = useTranslation();
	const appTheme = useTheme();
	if (loading) {
		return (
			<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
				<FadeSpinner size={51} height={15} width={6} borderRadius={3} />
			</Box>
		);
	}

	const totalFamilies = data?.find((item) => item.status === 'Total')?.count;
	const surveysSent = data?.find((item) => item.status === SurveyStatsStatusEnum.Sent)?.count;
	const surveysDelivered = data?.find((item) => item.status === SurveyStatsStatusEnum.Delivered)?.count;
	const surveysCompleted = data?.find((item) => item.status === SurveyStatsStatusEnum.Completed)?.count;

	const surveyFunnelRowsData: SurveyFunnelRowProps[] = [
		{
			label: 'Total Families',
			percentage: 100,
			familiesAmount: totalFamilies ?? 0,
			color: AnalyticsChartColors.Yellow,
		},
		{
			label: 'Surveys Sent',
			percentage: surveysSent && totalFamilies ? (surveysSent / totalFamilies) * 100 : 0,
			familiesAmount: surveysSent ?? 0,
			color: AnalyticsChartColors.Coral,
		},
		{
			label: 'Delivered',
			percentage: surveysDelivered && totalFamilies ? (surveysDelivered / totalFamilies) * 100 : 0,
			familiesAmount: surveysDelivered ?? 0,
			color: AnalyticsChartColors.SeaGreen,
		},
		{
			label: 'Completed',
			percentage: surveysCompleted && totalFamilies ? (surveysCompleted / totalFamilies) * 100 : 0,
			familiesAmount: surveysCompleted ?? 0,
			color: AnalyticsChartColors.Sky,
		},
	];

	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500, pb: '12px' }}>
				Survey Engagement Funnel
			</Typography>
			{data && data?.length ? (
				<Box sx={{ height: '100%', display: 'grid', gridTemplateRows: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
					{surveyFunnelRowsData.map((row) => (
						<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 0.3 }}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
								<Typography color={appTheme.colors.grayLightBlue} fontWeight={500}>
									{row.label}
								</Typography>
								<Typography color={appTheme.colors.grayLightBlue} fontSize={'13px'}>{`${
									row.familiesAmount
								} families (${row.percentage.toFixed(0)}%)`}</Typography>
							</Box>
							<Box
								sx={{
									position: 'relative',
									height: '100%',
									transition: 'transform 700ms ease',
								}}
							>
								<LinearProgress
									variant="determinate"
									value={Math.max(0, Math.min(100, row.percentage))}
									sx={{
										height: '100%',
										borderRadius: '16px',
										backgroundColor: (theme) => theme.colors.grayLight,
										'& .MuiLinearProgress-bar': {
											backgroundColor: row.color,
											borderRadius: '16px',
										},
									}}
								/>
								<Typography
									sx={{
										position: 'absolute',
										top: '50%',
										right: '4%',
										transform: 'translateY(-50%)',
									}}
								>{`${Math.round(row.percentage)}%`}</Typography>
							</Box>
						</Box>
					))}
				</Box>
			) : (
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
					<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500 }}>
						No data found for the chart
					</Typography>
				</Box>
			)}
		</Box>
	);
};
